import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
// eslint-disable-next-line no-restricted-imports
import { apiClient } from "../../../../../shared/api/apiClient";
import { useSocket } from '../../../../../shared/socket/useSocket';

export default function WhatsBookingSessionCard({ shortCode, isReceived }) {
    const [sessionData, setSessionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useSocket(useMemo(() => ({
        'booking-session-confirmed': (data) => {
            if (data && data.shortCode === shortCode) {
                setSessionData(prev => prev ? { ...prev, status: 'confirmed' } : prev);
            }
        }
    }), [shortCode]));

    useEffect(() => {
        let mounted = true;
        const fetchSession = async () => {
            try {
                // Must include /api so Nginx proxies it to backend
                const res = await apiClient.get(`/api/booking/sessions/${shortCode}`);
                if (mounted) {
                    if (res.success && res.session) {
                        setSessionData(res.session);
                    } else {
                        setError(true);
                    }
                    setLoading(false);
                }
            } catch (err) {
                console.error("[WhatsBookingSessionCard] Error fetching session:", shortCode, err);
                if (mounted) {
                    setError(true);
                    setLoading(false);
                }
            }
        };
        fetchSession();
        return () => { mounted = false; };
    }, [shortCode]);

    if (loading) {
        return (
            <div style={{ padding: "12px", backgroundColor: "rgba(0,0,0,0.03)", borderRadius: "8px", marginTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#888" }}>Cargando cita...</span>
            </div>
        );
    }

    if (error || !sessionData) {
        return (
            <div style={{
                marginTop: "8px",
                border: `1px solid #9ca3af`,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: isReceived ? '#ffffff' : '#dcf8c6',
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
            }}>
                <div style={{
                    backgroundColor: '#9ca3af',
                    color: '#fff',
                    padding: "8px 12px",
                    fontSize: "13px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}>
                    <XCircle size={16} />
                    Enlace de cita no válido o expirado
                </div>
                <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ color: "#6b7280", fontSize: "12px", fontStyle: "italic" }}>
                        La sesión no se pudo cargar. Es posible que el enlace esté incorrecto o haya caducado.
                    </div>
                </div>
            </div>
        );
    }

    const { status, duration, hostUsername, expiresAt } = sessionData;
    let isExpired = false;
    if (status === 'pending' && expiresAt) {
        if (new Date(expiresAt) - new Date() <= 0) {
            isExpired = true;
        }
    }

    const isPending = status === 'pending' && !isExpired;
    const isConfirmed = status === 'confirmed';

    let timeRemainingText = "";
    if (isPending && expiresAt) {
        const diffMs = new Date(expiresAt) - new Date();
        if (diffMs > 0) {
            const diffMins = Math.floor(diffMs / 60000);
            if (diffMins > 60) {
                const diffHours = Math.floor(diffMins / 60);
                timeRemainingText = `Expira en ${diffHours} h`;
            } else {
                timeRemainingText = `Expira en ${diffMins} min`;
            }
        }
    }

    return (
        <div style={{
            marginTop: "8px",
            border: `1px solid ${isConfirmed ? '#10b981' : isPending ? '#3b82f6' : '#9ca3af'}`,
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: isReceived ? '#ffffff' : '#dcf8c6',
            boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
        }}>
            <div style={{
                backgroundColor: isConfirmed ? '#10b981' : isPending ? '#3b82f6' : '#9ca3af',
                color: '#fff',
                padding: "8px 12px",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {isConfirmed ? <CheckCircle size={16} /> : isPending ? <Calendar size={16} /> : <XCircle size={16} />}
                    {isConfirmed ? "Cita Confirmada" : isPending ? "Cita sin confirmar" : "Cita expirada"}
                </div>
                {timeRemainingText && (
                    <span style={{ fontSize: "11px", backgroundColor: "rgba(0,0,0,0.15)", padding: "2px 6px", borderRadius: "10px" }}>
                        {timeRemainingText}
                    </span>
                )}
            </div>
            
            <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#374151", fontSize: "13px" }}>
                    <Clock size={16} color="#6b7280" />
                    <span>Duración: {duration} min</span>
                </div>
                
                {isPending && hostUsername && (
                    <a 
                        href={`/p/${hostUsername}?meet${duration}/${shortCode}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            marginTop: "4px",
                            padding: "8px",
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontSize: "13px",
                            fontWeight: "500"
                        }}
                    >
                        Agendar ahora <ArrowRight size={14} />
                    </a>
                )}
            </div>
        </div>
    );
}
